import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Loader2,
  Lock,
  MapPin,
} from "lucide-react";
import { formatDayDate, formatTimeRange } from "../lib/datetime";
import { teacherPDDayPath } from "../lib/pdDayRoutes";
import { prepareTeacherPDDaySummary } from "./useTeacherPDDaySummary";
import type { PDDayGroup } from "./PDDayRow";
import type { PDDaySummary } from "../types";

// The teacher's PD-day card (facilitators keep PDDayRow). Each group gets
// its own card shape and colour: past days collapse to a
// compact muted-red "done" row with a Review action, today's day is a
// blue-outlined card that shows its details and Start button directly,
// and future days are dimmed white rows with an amber pill. Future
// sessions follow the teacher app's locked-module pattern: a real but
// disabled button — visible, dimmed, no navigation on tap — rather than a
// link that silently does nothing.
export function TeacherPDDayCard({
  day,
  group,
  dayNumber,
}: {
  day: PDDaySummary;
  group: PDDayGroup;
  dayNumber?: number;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const future = group === "future";
  // Today's and past days both open their modules (the header saves them
  // to the phone in the background); a past PD Day stays open so teachers
  // can revisit and change their work. See teacherPDDayPath.
  // groupPDDays files a day as past once its scheduled end has passed.
  const finished = group === "past";

  const target = teacherPDDayPath(day, finished);

  async function openDay() {
    if (opening || future) return;
    if (finished && !day.modules?.length) {
      setOpening(true);
      await prepareTeacherPDDaySummary(day.id);
      if (!mounted.current) return;
    }
    navigate(target);
  }

  const title = dayNumber ? t("pdDayOrdinal", { number: dayNumber }) : t("pdDay");
  const date = formatDayDate(day.start, i18n.language);
  const time = formatTimeRange(day.start, day.end, i18n.language);

  if (group === "active") {
    const running = day.status === "in_progress";
    return (
      <div className="w-full rounded-xl border-2 border-accent/40 bg-surface shadow-card">
        <div className="flex w-full items-center justify-between gap-3 p-4 text-start">
          <span className="flex min-w-0 items-center gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent text-white">
              <BookOpen size={16} />
            </span>
            <span className="grid min-w-0">
              <span className="flex flex-wrap items-center gap-2">
                <strong className="text-base font-bold text-app-ink">{title}</strong>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${running ? "bg-success-bg text-success-text" : "bg-accent-soft text-accent"}`}
                >
                  {running ? t("pdDayActive") : t("todaySection")}
                </span>
              </span>
              <span className="truncate text-xs text-app-muted">{date}</span>
            </span>
          </span>
        </div>

        <div className="flex flex-col gap-4 px-4 pb-4">
          <div className="grid gap-1.5 rounded-lg bg-app-field p-3 text-sm text-app-copy">
            <span className="inline-flex items-center gap-1.5" dir="ltr">
              <Clock size={14} className="shrink-0 text-app-icon" />
              {time}
            </span>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <MapPin size={14} className="shrink-0 text-app-icon" />
              <span className="truncate">{day.center}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="grid">
              <span className="text-[11px] text-app-muted">{t("pdDayStatusLabel")}</span>
              <span className="text-sm font-semibold text-app-ink">
                {running ? t("statusInProgress") : t("statusReady")}
              </span>
            </span>
            <button
              type="button"
              className="inline-flex min-h-12 items-center gap-1.5 rounded-xl bg-accent px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-accent-hover active:scale-[0.98] disabled:cursor-wait disabled:opacity-80"
              onClick={() => void openDay()}
              disabled={opening}
              aria-busy={opening}
            >
              {opening ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  {t("pdDayStart")}
                  <ArrowRight size={18} className="rtl:rotate-180" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`flex w-full items-center justify-between gap-3 rounded-xl p-4 text-start transition-colors ${future ? "bg-surface shadow-raised disabled:cursor-not-allowed disabled:opacity-60" : "border border-archive-ink/10 bg-archive-tint hover:bg-danger-bg disabled:cursor-wait"}`}
      onClick={future ? undefined : () => void openDay()}
      disabled={future || opening}
      aria-busy={opening}
    >
      <span className="flex min-w-0 items-center gap-3">
        {future ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-app-field text-app-icon">
            <Lock size={16} />
          </span>
        ) : (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-archive-ink text-white">
            <Check size={16} strokeWidth={3} />
          </span>
        )}
        <span className="grid min-w-0">
          <strong className="text-base font-bold text-app-ink">{title}</strong>
          <span className="truncate text-xs text-app-muted">{date}</span>
        </span>
      </span>

      {future ? (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-bg px-2.5 py-1 text-[11px] font-semibold text-warning-text">
          <CalendarDays size={13} />
          {t("pdDayComingSoon")}
        </span>
      ) : (
        <span className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-lg bg-surface px-3 text-xs font-semibold text-archive-ink shadow-raised">
          {opening ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              {t("pdDayReview")}
              <ChevronRight size={16} className="rtl:rotate-180" />
            </>
          )}
        </span>
      )}
    </button>
  );
}
